import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '@/shell/AppShell'
import { Field } from '@/components/forge/Field'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { PlaceFilter } from '@/components/forge/PlaceFilter'
import { EmptyState, SkeletonRows } from '@/components/forge/States'
import { Eyebrow, Num } from '@/components/forge/primitives'
import { CHART_TIERS, MAX_TAGS_PER_CHART, NAME_MAX_LENGTH } from '@/features/sensorCharts/api'
import {
  useCreateSensorChart,
  useLocationSensorTree,
  useSensorChartAccess,
} from '@/features/sensorCharts/queries'
import { pickTags } from '@/features/sensorCharts/tree'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useToast } from '@/providers/ToastProvider'
import { ChartWriteError, SensorBrowser } from './SensorChartParts'
import { SensorTagTree } from './SensorTagTree'

/**
 * New sensor chart. LIVE.
 *
 * Three steps on one screen rather than a wizard, because they are not
 * sequential once begun: a reader choosing tags goes back to the location, and
 * a wizard's Back button would be the only way there.
 *
 *   1. A place — through `PlaceFilter`, the same `HierarchyPicker` the anomaly
 *      list and the top bar use, limited to plant, line and machine.
 *   2. Its sensors and everything beneath it, as a tree, each tag ticked on its
 *      own. A tag is what the API stores; a sensor is only where it lives.
 *   3. The chosen tags in the same tree, pruned, with a Remove per tag, and the
 *      name the API requires.
 *
 * ── Changing the place clears the tags ───────────────────────────────────
 * The server refuses any tag whose sensor is not at the chart's place or
 * beneath it, so a tick carried across a change of place is a guaranteed 400.
 * Said beside the control rather than discovered on Create.
 *
 * ── What is not asked for ─────────────────────────────────────────────────
 * No time range and no chart type: `SensorChartCreate` has neither. A span is
 * chosen on the chart itself and lives in its URL.
 */
export function NewSensorChartPage() {
  const { t } = useTranslation(['app', 'common'])
  const navigate = useNavigate()
  const { toast } = useToast()
  const access = useSensorChartAccess()
  const create = useCreateSensorChart()

  const [place, setPlace] = useState(null)
  const [selected, setSelected] = useState(() => new Set())
  const [name, setName] = useState('')
  const [tried, setTried] = useState(false)

  useDocumentTitle(`${t('sensors.charts.newTitle')} · ${t('common:brand.name')}`)

  const browser = useLocationSensorTree(place, { enabled: access.browse })
  const review = useMemo(() => pickTags(browser.tree, selected), [browser.tree, selected])

  const toggle = (tagId) =>
    setSelected((previous) => {
      const next = new Set(previous)
      if (next.has(tagId)) next.delete(tagId)
      else if (next.size < MAX_TAGS_PER_CHART) next.add(tagId)
      return next
    })

  const choosePlace = (next) => {
    if (next?.id === place?.id) return
    setPlace(next)
    setSelected(new Set())
    create.reset()
  }

  const nameError =
    tried && !name.trim()
      ? t('sensors.charts.validation.nameRequired')
      : name.trim().length > NAME_MAX_LENGTH
        ? t('sensors.charts.validation.nameTooLong', { max: NAME_MAX_LENGTH })
        : null
  const tagsError = tried && selected.size === 0 ? t('sensors.charts.validation.tagsRequired') : null

  const submit = async () => {
    setTried(true)
    if (!place || !name.trim() || name.trim().length > NAME_MAX_LENGTH || selected.size === 0) return
    if (create.isPending) return
    try {
      const chart = await create.mutateAsync({ name, place, sensorTagIds: [...selected] })
      toast(t('sensors.charts.createdToast', { name: chart.name }), 'ok')
      navigate(`/app/sensors/charts/${chart.id}`)
    } catch {
      // Rendered from `create.error` beside the button.
    }
  }

  if (access.isPending) {
    return (
      <Page>
        <SkeletonRows count={4} />
      </Page>
    )
  }

  return (
    <Page>
      <header className="mb-4">
        <p className="m-0 mb-1.5">
          <Link
            to="/app/sensors"
            className="inline-flex items-center gap-1.5 text-body-sm text-ink-dim transition-colors hover:text-ink"
          >
            <Icon name="right" size="xs" className="rotate-180 mirror-rtl" />
            {t('sensors.workstation.allCharts')}
          </Link>
        </p>
        <h1 className="m-0 text-display text-balance">{t('sensors.charts.newTitle')}</h1>
        <p className="mt-2 mb-0 max-w-[70ch] text-body text-ink-dim">{t('sensors.charts.newSubtitle')}</p>
      </header>

      {!access.charts ? (
        <EmptyState icon="lock" title={t('sensors.charts.noAccess')} body={t('sensors.charts.noAccessBody')} />
      ) : (
        <div className="grid items-start gap-3.5 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,26rem)]">
          <div className="flex min-w-0 flex-col gap-3.5">
            <Panel>
              <Eyebrow className="mb-2.5">{t('sensors.charts.step.place')}</Eyebrow>
              <div className="flex flex-wrap items-center gap-3">
                <PlaceFilter
                  id="chart-place"
                  value={place}
                  onChange={choosePlace}
                  allowTiers={CHART_TIERS}
                  label={t('sensors.charts.placeLabel')}
                  emptyLabel={t('sensors.charts.placeEmpty')}
                  title={t('sensors.charts.placeTitle')}
                  note={t('sensors.charts.placeNote')}
                  disabled={create.isPending}
                />
                {place && selected.size ? (
                  <p className="m-0 text-micro text-ink-mute">{t('sensors.charts.placeClears')}</p>
                ) : null}
              </div>
              {place ? <PlacePath place={place} /> : null}
            </Panel>

            <Panel>
              <Eyebrow className="mb-2.5">{t('sensors.charts.step.tags')}</Eyebrow>
              {!place ? (
                <EmptyState
                  icon="factory"
                  title={t('sensors.charts.pickPlaceFirst')}
                  body={t('sensors.charts.pickPlaceFirstBody')}
                  className="border-0 shadow-none"
                />
              ) : (
                <SensorBrowser
                  access={access}
                  browser={browser}
                  selected={selected}
                  onToggle={toggle}
                  atCapacity={selected.size >= MAX_TAGS_PER_CHART}
                  disabled={create.isPending}
                />
              )}
            </Panel>
          </div>

          <Panel className="flex flex-col gap-4 lg:sticky lg:top-4">
            <Eyebrow>{t('sensors.charts.step.review')}</Eyebrow>
            <Field
              label={t('sensors.charts.nameLabel')}
              required
              value={name}
              maxLength={NAME_MAX_LENGTH}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('sensors.charts.namePlaceholder')}
              error={nameError}
              disabled={create.isPending}
            />

            <div>
              <p className="m-0 mb-2 flex items-center gap-2 text-control text-ink">
                {t('sensors.charts.selectedSignals')}
                <Num className="ms-auto text-micro font-normal text-ink-dim">
                  {t('sensors.charts.capacity', { count: selected.size, max: MAX_TAGS_PER_CHART })}
                </Num>
              </p>
              {review ? (
                <div className="max-h-[50vh] overflow-y-auto rounded-sm border border-hairline">
                  <SensorTagTree
                    key={review.key}
                    tree={review}
                    mode="review"
                    unitSymbol={browser.unitSymbol}
                    onRemove={toggle}
                    disabled={create.isPending}
                  />
                </div>
              ) : (
                <p className="m-0 rounded-sm border border-dashed border-hairline px-3.5 py-6 text-center text-body-sm text-ink-dim">
                  {t('sensors.charts.noneSelected')}
                </p>
              )}
              {tagsError ? (
                <p role="alert" className="mt-2 mb-0 text-body-sm text-on-red">
                  {tagsError}
                </p>
              ) : null}
              {tried && !place ? (
                <p role="alert" className="mt-2 mb-0 text-body-sm text-on-red">
                  {t('sensors.charts.validation.placeRequired')}
                </p>
              ) : null}
            </div>

            <ChartWriteError error={create.error} action="create" />

            <div className="flex flex-wrap justify-end gap-2">
              <ForgeButton asChild>
                <Link to="/app/sensors">{t('common:actions.cancel')}</Link>
              </ForgeButton>
              <ForgeButton variant="primary" onClick={submit} disabled={create.isPending}>
                {create.isPending ? t('sensors.charts.creating') : t('sensors.charts.create')}
              </ForgeButton>
            </div>
          </Panel>
        </div>
      )}
    </Page>
  )
}

/** Where the chosen place sits: the trail it was chosen from, then the place. */
function PlacePath({ place }) {
  const segments = [...(place.trail ?? []).filter((node) => node.tier !== 'enterprise'), place]
  return (
    <p className="mt-2.5 mb-0 flex flex-wrap items-center gap-x-1.5 text-body-sm text-ink-dim">
      {segments.map((node, index) => (
        <span key={node.id} className="flex items-center gap-1.5">
          {index > 0 ? <Icon name="right" size="xs" className="mirror-rtl text-ink-mute" /> : null}
          <span className={index === segments.length - 1 ? 'font-semibold text-ink' : undefined}>
            {node.name}
          </span>
        </span>
      ))}
    </p>
  )
}
